import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { register as registerMember } from '../../services/member-auth.service';
import Button from '../../components/common/Button';
import { OBRIGACAO_ABIAN, OBRIGACOES_OPTIONS } from '../../constants/obrigacoes';
import { CheckCircle, AlertCircle } from 'lucide-react';

const MemberRegister = () => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      obrigacoes_pagas: [],
    },
  });

  const password = watch('password');
  const obrigacoesSelecionadas = watch('obrigacoes_pagas') || [];
  const isAbian = obrigacoesSelecionadas.includes(OBRIGACAO_ABIAN);

  const toggleObrigacao = (obrigacao) => {
    const current = getValues('obrigacoes_pagas') || [];
    if (obrigacao === OBRIGACAO_ABIAN && !current.includes(obrigacao)) {
      clearErrors(['feitura_date', 'idade_de_santo']);
    }
    if (current.includes(obrigacao)) {
      setValue('obrigacoes_pagas', current.filter((o) => o !== obrigacao), { shouldValidate: true });
    } else {
      setValue('obrigacoes_pagas', [...current, obrigacao], { shouldValidate: true });
    }
  };

  const onSubmit = async (data) => {
    setError('');
    setIsLoading(true);

    try {
      const payload = {
        name: data.name,
        email: data.email,
        password: data.password,
        password_confirmation: data.password_confirmation,
        feitura_date: data.feitura_date || null,
        idade_de_santo: Number.isNaN(data.idade_de_santo) ? null : data.idade_de_santo,
        orixa: data.orixa,
        junto: data.junto,
        obrigacoes_pagas: data.obrigacoes_pagas,
      };

      if (data.cpf) payload.cpf = data.cpf;
      if (data.phone) payload.phone = data.phone;
      if (data.birth_date) payload.birth_date = data.birth_date;

      await registerMember(payload);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Erro ao realizar cadastro.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-brand-brown flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" className="flex justify-center">
            <img src="/assets/logo-branco.png" alt="Ilê Asé Sòpònnón" className="h-16 w-auto" />
          </Link>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-10 px-6 shadow-xl border-2 border-brand-beige sm:rounded-2xl sm:px-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-display font-bold text-brand-brown mb-2">
              Cadastro realizado!
            </h2>
            <p className="text-gray-600 mb-6">
              Seu cadastro foi recebido e está aguardando aprovação. Você será notificado quando
              puder acessar a área do associado.
            </p>
            <Link
              to="/associado/login"
              className="inline-flex justify-center py-2 px-6 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-brown hover:opacity-90"
            >
              Ir para o login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-brown flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
        <Link to="/" className="flex justify-center">
          <img src="/assets/logo-branco.png" alt="Ilê Asé Sòpònnón" className="h-16 w-auto" />
        </Link>
        <h2 className="mt-8 text-center font-display text-3xl font-bold text-brand-cream">
          Cadastro de Associado
        </h2>
        <p className="mt-3 text-center text-base text-brand-beige">
          Preencha seus dados para solicitar o cadastro
        </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-white py-10 px-6 shadow-xl border-2 border-brand-beige sm:rounded-2xl sm:px-12">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Seção 1 — Dados de Acesso */}
            <div>
              <h3 className="font-semibold text-brand-brown text-lg mb-4 pb-2 border-b border-brand-beige">
                Dados de Acesso
              </h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="label">
                    Nome completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    className={`input ${errors.name ? 'input-error' : ''}`}
                    {...register('name', {
                      required: 'Nome é obrigatório',
                      minLength: { value: 2, message: 'Nome deve ter no mínimo 2 caracteres' },
                    })}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="label">
                    E-mail <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`input ${errors.email ? 'input-error' : ''}`}
                    {...register('email', {
                      required: 'E-mail é obrigatório',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'E-mail inválido',
                      },
                    })}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="password" className="label">
                      Senha <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="password"
                      type="password"
                      className={`input ${errors.password ? 'input-error' : ''}`}
                      {...register('password', {
                        required: 'Senha é obrigatória',
                        minLength: { value: 6, message: 'Mínimo 6 caracteres' },
                      })}
                    />
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="password_confirmation" className="label">
                      Confirmar senha <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="password_confirmation"
                      type="password"
                      className={`input ${errors.password_confirmation ? 'input-error' : ''}`}
                      {...register('password_confirmation', {
                        required: 'Confirmação de senha é obrigatória',
                        validate: (value) => value === password || 'As senhas não conferem',
                      })}
                    />
                    {errors.password_confirmation && (
                      <p className="mt-1 text-sm text-red-600">{errors.password_confirmation.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Seção 2 — Dados Pessoais */}
            <div>
              <h3 className="font-semibold text-brand-brown text-lg mb-4 pb-2 border-b border-brand-beige">
                Dados Pessoais
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="cpf" className="label">CPF</label>
                  <input
                    id="cpf"
                    type="text"
                    className="input"
                    placeholder="000.000.000-00"
                    {...register('cpf')}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="label">Telefone</label>
                  <input
                    id="phone"
                    type="text"
                    className="input"
                    placeholder="(31) 99999-9999"
                    {...register('phone')}
                  />
                </div>

                <div>
                  <label htmlFor="birth_date" className="label">Data de nascimento</label>
                  <input
                    id="birth_date"
                    type="date"
                    className="input"
                    {...register('birth_date')}
                  />
                </div>
              </div>
            </div>

            {/* Seção 3 — Dados de Candomblé */}
            <div>
              <h3 className="font-semibold text-brand-brown text-lg mb-1 pb-2 border-b border-brand-beige">
                Dados de Candomblé
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {isAbian
                  ? 'Abian: data de feitura e idade de santo são opcionais.'
                  : 'Todos os campos desta seção são obrigatórios.'}
              </p>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="feitura_date" className="label">
                      Data de feitura {!isAbian && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      id="feitura_date"
                      type="date"
                      className={`input ${errors.feitura_date ? 'input-error' : ''}`}
                      {...register('feitura_date', {
                        required: isAbian ? false : 'Data de feitura é obrigatória',
                      })}
                    />
                    {errors.feitura_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.feitura_date.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="idade_de_santo" className="label">
                      Idade de santo (anos) {!isAbian && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      id="idade_de_santo"
                      type="number"
                      min="0"
                      className={`input ${errors.idade_de_santo ? 'input-error' : ''}`}
                      {...register('idade_de_santo', {
                        required: isAbian ? false : 'Idade de santo é obrigatória',
                        min: { value: 0, message: 'Idade de santo não pode ser negativa' },
                        valueAsNumber: true,
                      })}
                    />
                    {errors.idade_de_santo && (
                      <p className="mt-1 text-sm text-red-600">{errors.idade_de_santo.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="orixa" className="label">
                      Orixá <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="orixa"
                      type="text"
                      className={`input ${errors.orixa ? 'input-error' : ''}`}
                      {...register('orixa', {
                        required: 'Orixá é obrigatório',
                      })}
                    />
                    {errors.orixa && (
                      <p className="mt-1 text-sm text-red-600">{errors.orixa.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="junto" className="label">
                      Juntó <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="junto"
                      type="text"
                      className={`input ${errors.junto ? 'input-error' : ''}`}
                      {...register('junto', {
                        required: 'Juntó é obrigatório',
                      })}
                    />
                    {errors.junto && (
                      <p className="mt-1 text-sm text-red-600">{errors.junto.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="label">
                    Obrigações pagas <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={`mt-2 p-4 rounded-lg border ${
                      errors.obrigacoes_pagas ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {OBRIGACOES_OPTIONS.map((obrigacao) => (
                        <label
                          key={obrigacao}
                          className="flex items-center gap-3 cursor-pointer group"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-brand-brown border-gray-300 rounded focus:ring-brand-brown cursor-pointer"
                            checked={obrigacoesSelecionadas.includes(obrigacao)}
                            onChange={() => toggleObrigacao(obrigacao)}
                          />
                          <span className="text-sm text-gray-700 group-hover:text-gray-900">
                            {obrigacao}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <input
                    type="hidden"
                    {...register('obrigacoes_pagas', {
                      validate: (value) =>
                        (Array.isArray(value) && value.length > 0) ||
                        'Selecione ao menos uma obrigação paga',
                    })}
                  />
                  {errors.obrigacoes_pagas && (
                    <p className="mt-1 text-sm text-red-600">{errors.obrigacoes_pagas.message}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={isLoading}
                disabled={isLoading}
              >
                Solicitar Cadastro
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            Já possui cadastro?{' '}
            <Link to="/associado/login" className="font-medium text-brand-brown hover:opacity-80">
              Faça login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberRegister;
